import { matterDetailQueryKey } from "@/lib/data/static/matter-query-key";
import {
  invalidateSearchQueries,
  isCanonicalSearchQuery,
} from "@/lib/data/react-query/search/invalidation";
import {
  createMatterV3,
  deleteMatter,
  retrieveMatterV3,
  updateMatter,
  updateMatterIncidentReportNotApplicableV2,
} from "@/lib/data/requests";
import { queryKeys } from "@/lib/data/static/query-keys";
import { type APIError, type APIResponse, type Matter } from "@/lib/types";
import { toastAPIErrorMessage } from "@/lib/utils/axiosUtils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { type AxiosError } from "axios";
import toast from "react-hot-toast";

export const retrieveMatterQueryOptions = (
  matterId: Parameters<typeof retrieveMatterV3>[0],
) => {
  return {
    queryKey: matterDetailQueryKey(matterId),
    enabled: !!matterId,
    queryFn: async () => retrieveMatterV3(matterId),
    fetchOnWindowFocus: false,
    retry: false,
  };
};

export const useCreateMatter = ({
  invalidateOnSuccess = true,
  showSuccessToast = true,
}: {
  invalidateOnSuccess?: boolean;
  showSuccessToast?: boolean;
} = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof createMatterV3>[0]) =>
      createMatterV3(data),
    onError: () => {
      toast.error("Error creating matter");
    },
    onSuccess: async (response, variables) => {
      if (showSuccessToast) {
        toast.success("Matter created successfully");
      }
      if (!invalidateOnSuccess) {
        return;
      }
      await invalidateSearchQueries(queryClient);
      await queryClient.invalidateQueries({
        predicate: (query) => !isCanonicalSearchQuery(query),
        queryKey: [queryKeys.matters],
      });
      await queryClient.invalidateQueries({
        predicate: (query) => !isCanonicalSearchQuery(query),
        queryKey: [queryKeys.matters, response.data.id],
      });
      await queryClient.invalidateQueries({
        predicate: (query) => !isCanonicalSearchQuery(query),
        queryKey: [queryKeys.matters, "matter-search-report"],
      });
      await queryClient.invalidateQueries({
        predicate: (query) => !isCanonicalSearchQuery(query),
        queryKey: [queryKeys.matters, "search"],
      });
      await queryClient.invalidateQueries({
        predicate: (query) => !isCanonicalSearchQuery(query),
        queryKey: [queryKeys.matters, "search-all"],
      });
      await queryClient.invalidateQueries({
        predicate: (query) => !isCanonicalSearchQuery(query),
        queryKey: [queryKeys.massTorts, "matter-search"],
      });
      if (variables.massTortId) {
        await queryClient.invalidateQueries({
          predicate: (query) => !isCanonicalSearchQuery(query),
          queryKey: [queryKeys.massTorts],
        });
      }

      setTimeout(() => {
        void queryClient.invalidateQueries({
          predicate: (query) => !isCanonicalSearchQuery(query),
          queryKey: [queryKeys.matters, response.data.id],
        });
        void queryClient.invalidateQueries({
          predicate: (query) => !isCanonicalSearchQuery(query),
          queryKey: [queryKeys.matters, "matter-search-report"],
        });
        void queryClient.invalidateQueries({
          predicate: (query) => !isCanonicalSearchQuery(query),
          queryKey: [queryKeys.matters, "search"],
        });
        void queryClient.invalidateQueries({
          predicate: (query) => !isCanonicalSearchQuery(query),
          queryKey: [queryKeys.matters, "search-all"],
        });
        void queryClient.invalidateQueries({
          predicate: (query) => !isCanonicalSearchQuery(query),
          queryKey: [queryKeys.massTorts, "matter-search"],
        });
        if (variables.massTortId) {
          void queryClient.invalidateQueries({
            predicate: (query) => !isCanonicalSearchQuery(query),
            queryKey: [queryKeys.massTorts],
          });
        }
      }, 5000);
    },
  });
};

type UseUpdateMatterOptions = {
  invalidateOnSettled?: boolean;
  successToast?: string | null;
};

export const useUpdateMatter = ({
  invalidateOnSettled = true,
  successToast = "Matter updated successfully",
}: UseUpdateMatterOptions = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof updateMatter>[0]) =>
      updateMatter(data),
    onError: (error: AxiosError<APIError>) => {
      toastAPIErrorMessage(error, "Error updating matter");
    },
    onSettled: async (_data, _error, variables) => {
      if (!invalidateOnSettled) {
        return;
      }
      await invalidateSearchQueries(queryClient);
      await queryClient.invalidateQueries({
        predicate: (query) => !isCanonicalSearchQuery(query),
        queryKey: [queryKeys.matters, variables.id],
      });
      await queryClient.invalidateQueries({
        predicate: (query) => !isCanonicalSearchQuery(query),
        queryKey: [queryKeys.matters, "search"],
      });
      await queryClient.invalidateQueries({
        predicate: (query) => !isCanonicalSearchQuery(query),
        queryKey: [queryKeys.matters, "search-all"],
      });
    },
    onSuccess: async (_response, variables) => {
      if (successToast) {
        toast.success(successToast);
      }
      if ("claimTypeIds" in variables) {
        await queryClient.invalidateQueries({
          predicate: (query) => !isCanonicalSearchQuery(query),
          queryKey: [queryKeys.matterPhases, variables.id],
        });
      }
      if (invalidateOnSettled) {
        await queryClient.invalidateQueries({
          predicate: (query) => !isCanonicalSearchQuery(query),
          queryKey: [queryKeys.matters],
        });
      }
    },
  });
};

export const useUpdateMatterIncidentReportNotApplicable = ({
  successToast = "Matter updated successfully",
}: UseUpdateMatterOptions = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (
      data: Parameters<typeof updateMatterIncidentReportNotApplicableV2>[0],
    ) => updateMatterIncidentReportNotApplicableV2(data),
    onMutate: (data) => {
      const previousData = queryClient.getQueryData(
        matterDetailQueryKey(data.matterId),
      );

      queryClient.setQueryData(
        matterDetailQueryKey(data.matterId),
        (oldData: APIResponse<Matter> | undefined) => {
          if (!oldData) return;
          return {
            ...oldData,
            data: {
              ...oldData.data,
              incidentReportNotApplicable: data.incidentReportNotApplicable,
            },
          };
        },
      );

      return { previousData };
    },
    onError: (error: AxiosError<APIError>, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          matterDetailQueryKey(variables.matterId),
          context.previousData,
        );
      }
      toastAPIErrorMessage(error, "Error updating matter");
    },
    onSettled: async (_data, _error, variables) => {
      await invalidateSearchQueries(queryClient);
      await queryClient.invalidateQueries({
        predicate: (query) => !isCanonicalSearchQuery(query),
        queryKey: [queryKeys.matters, variables.matterId],
      });
    },
    onSuccess: async () => {
      if (successToast) {
        toast.success(successToast);
      }
      await queryClient.invalidateQueries({
        predicate: (query) => !isCanonicalSearchQuery(query),
        queryKey: [queryKeys.matters],
      });
    },
  });
};

export const useDeleteMatter = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof deleteMatter>[0]) =>
      deleteMatter(data),
    onError: (error: AxiosError<APIError>) => {
      toastAPIErrorMessage(error, "Error deleting matter");
    },
    onSettled: async () => {
      await invalidateSearchQueries(queryClient);
      await queryClient.invalidateQueries({
        predicate: (query) => !isCanonicalSearchQuery(query),
        queryKey: [queryKeys.matters],
      });
      await queryClient.invalidateQueries({
        predicate: (query) => !isCanonicalSearchQuery(query),
        queryKey: [queryKeys.matters, "search"],
      });
      await queryClient.invalidateQueries({
        predicate: (query) => !isCanonicalSearchQuery(query),
        queryKey: [queryKeys.matters, "search-all"],
      });
    },
    onSuccess: () => {
      toast.success("Matter deleted successfully");
    },
  });
};
